import React, { useState } from "react";
import { observer } from "mobx-react";
import { useAwards } from "@/hooks/useCMSData";
import { useGetMyAwards } from "@/hooks/useAwards";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import ClimberReport from "@/components/certificate-pdf";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Stack,
  Collapse,
} from "@mui/material";

export const CertificatesDebug = observer(() => {
  const { awards: cmsAwards, loading } = useAwards();
  const { data: myAwards = [], isLoading: myAwardsLoading } = useGetMyAwards();
  const currentUser = useCurrentUser();

  const certificateAwards = cmsAwards.filter(
    (a) => a.awardType === "certificate",
  );

  if (loading || myAwardsLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Certificates Debug
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        All certificate-type awards from CMS. Preview PDFs for any certificate,
        including unearned ones.
      </Typography>

      <Grid container spacing={3}>
        {certificateAwards.map((award) => (
          <CertificateCard
            key={award.id}
            award={award}
            myAwards={myAwards}
            currentUser={currentUser}
          />
        ))}
      </Grid>

      {certificateAwards.length === 0 && (
        <Typography color="text.secondary">
          No certificate-type awards found in CMS.
        </Typography>
      )}
    </Box>
  );
});

const CertificateCard = ({ award, myAwards, currentUser }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const completionRecord = myAwards.find(
    (a) => String(a.awardID) === String(award.id),
  );
  const awarded = !!completionRecord;
  const config = award.certificateConfig || {};
  const rawMetadata = config.metadata;
  const metadata =
    typeof rawMetadata === "string"
      ? (() => {
          try {
            return JSON.parse(rawMetadata);
          } catch {
            return {};
          }
        })()
      : rawMetadata || {};

  const completionDate = awarded
    ? new Date(completionRecord.completedAt + "T04:00:00Z").toLocaleDateString(
        "en-US",
        { month: "2-digit", day: "2-digit", year: "numeric" },
      )
    : new Date().toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });

  return (
    <Grid item xs={12}>
      <Card
        sx={{
          border: awarded ? "2px solid" : "1px solid",
          borderColor: awarded ? "success.main" : "divider",
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography variant="h6">
                {award.name}{" "}
                <Typography component="span" color="text.secondary">
                  (#{award.id})
                </Typography>
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 1 }}>
                <Chip
                  size="small"
                  label={awarded ? "Earned" : "Not Earned"}
                  color={awarded ? "success" : "default"}
                />
                {config.theme && (
                  <Chip size="small" label={`Theme: ${config.theme}`} />
                )}
                {metadata.backgroundColor && (
                  <Chip
                    size="small"
                    label={metadata.backgroundColor}
                    sx={{
                      backgroundColor: metadata.backgroundColor,
                      color:
                        metadata.backgroundColor === "#750937"
                          ? "white"
                          : "inherit",
                    }}
                  />
                )}
                {metadata.estimatedTrainingHours && (
                  <Chip
                    size="small"
                    label={`${metadata.estimatedTrainingHours}h training`}
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {award.title}
              </Typography>
            </Box>

            {award.image && (
              <img
                src={award.image.url}
                alt={award.title}
                style={{ width: 64, height: 64, objectFit: "contain" }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowConfig((v) => !v)}
            >
              {showConfig ? "Hide" : "Show"} CMS Config
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowPreview((v) => !v)}
            >
              {showPreview ? "Hide" : "Show"} PDF Preview
            </Button>
            <PDFDownloadLink
              document={
                <ClimberReport
                  award={award}
                  completionDate={completionDate}
                  currentUser={currentUser}
                />
              }
              fileName={`Certificate_${award.name.replace(/\s+/g, "_")}.pdf`}
            >
              {({ loading: pdfLoading }) => (
                <Button size="small" variant="contained" disabled={pdfLoading}>
                  {pdfLoading ? "Generating..." : "Download PDF"}
                </Button>
              )}
            </PDFDownloadLink>
          </Stack>

          <Collapse in={showConfig}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "grey.100",
                borderRadius: 1,
                overflow: "auto",
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontFamily: "monospace", fontSize: "0.8rem", m: 0 }}
              >
                {JSON.stringify(config, null, 2)}
              </Typography>
            </Box>
          </Collapse>

          <Collapse in={showPreview}>
            <Box
              sx={{
                mt: 2,
                height: 500,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <PDFViewer width="100%" height="100%" showToolbar={false}>
                <ClimberReport
                  award={award}
                  completionDate={completionDate}
                  currentUser={currentUser}
                />
              </PDFViewer>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default CertificatesDebug;
